import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetProfileData } from "@/features/shared/api/use-get-user-info";
import { useCustomerContextDefinitionsQuery } from "@/features/user/api/use-customer-context-definitions-query";
import CustomerPhotoSection from "@/features/user/components/customer-info/customer-photo-section";
import EditableUserSelect from "@/features/user/components/customer-info/editable-user-select";
import { CUSTOMER_INFO_USER_SEARCH_PARAM } from "@/features/user/customer-info-search-param";
import { EditUserForm } from "@/features/user/forms/edit-user-form";
import { getStaticFieldState } from "@/features/user/forms/get-static-field-state";
import { useSelectedUser } from "@/features/user/use-selected-party";
import { useLng } from "@/hooks/use-lng";
import { appTitleHead } from "@/lib/app-document-title";
import { useTranslation } from "@/lib/i18n/client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useWindowSize } from "@uidotdev/usehooks";
import { useAuth } from "oidc-react";
import { startTransition } from "react";

type CustomerInfoSearch = {
  user?: string;
};

export const Route = createFileRoute("/_app/account/_auth/customerinfo")({
  validateSearch: (search: Record<string, unknown>): CustomerInfoSearch => {
    const raw = search[CUSTOMER_INFO_USER_SEARCH_PARAM];
    const user = typeof raw === "string" && raw.length > 0 ? raw : undefined;
    return user !== undefined ? { user } : {};
  },
  head: () => appTitleHead("customer-info-title"),
  component: CustomerInfoPage
});

function CustomerInfoPage() {
  const lng = useLng();
  const navigate = useNavigate();
  const { user: paramUser } = Route.useSearch();
  const { t } = useTranslation(lng, "translation");
  const size = useWindowSize();
  const { userData } = useAuth();
  const token = userData?.access_token ?? "";
  const { isPending: isProfilePending, isError: isProfileError } = useGetProfileData(token);

  const { editableUsers, setSelectedUserId } = useSelectedUser();

  const selectedUser =
    (paramUser ? editableUsers.find((u) => u.id === paramUser) : null) ?? editableUsers[0] ?? null;

  const handleEditableUserChange = (userId: string) => {
    setSelectedUserId(userId);
    startTransition(() => {
      void navigate({
        to: "/account/customerinfo",
        search: { user: userId },
        replace: true
      });
    });
  };

  if (!size?.width) return null;

  const profileReady = selectedUser?.addresses !== undefined;
  if (!selectedUser || (!profileReady && isProfilePending && !isProfileError)) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const isManagingOthers = editableUsers.length > 1;
  const selectedIsSelf = Array.isArray(selectedUser.manages);
  const context = selectedIsSelf ? "BookingResponsible" : "BookingParticipant";

  return (
    <div className="flex h-full flex-col justify-between">
      <div className="w-full space-y-4">
        <h1 className="hidden text-left md:block">{t("customer-info-title")}</h1>
        <p className="mb-4">
          {t("customer-info-description")}.{" "}
          {isManagingOthers ? t("save-customer-info-multi") : t("save-customer-info")}
        </p>
        {isManagingOthers && (
          <EditableUserSelect
            editableUsers={editableUsers}
            selectedUserId={selectedUser.id}
            onValueChange={handleEditableUserChange}
          />
        )}
        <CustomerPhotoCard
          key={`${selectedUser.id}|${context}`}
          selectedUser={selectedUser}
          context={context}
          buttonSize={size.width >= 769 ? "default" : "lg"}
        />
        <Card className="rounded-2xl p-4 md:p-6">
          <EditUserForm
            key={`${selectedUser.id}|${profileReady ? "profile" : "session"}`}
            selectedUser={selectedUser}
          />
        </Card>
      </div>
    </div>
  );
}

function CustomerPhotoCard({
  selectedUser,
  context,
  buttonSize
}: {
  selectedUser: { id: string; name: string; photo?: string };
  context: "BookingResponsible" | "BookingParticipant";
  buttonSize: "default" | "lg";
}) {
  const { data, isPending, isError } = useCustomerContextDefinitionsQuery({ context });

  if (isPending) {
    return (
      <Card className="align-items-center flex gap-4 rounded-2xl p-4 md:p-6">
        <div className="flex w-full items-center gap-4" aria-hidden="true">
          <Skeleton className="h-20 w-20 rounded-full md:h-24 md:w-24" />
          <Skeleton className="h-10 w-32" />
        </div>
      </Card>
    );
  }

  if (isError || !data) {
    return null;
  }

  const definition = data.find((item) => item.context === context);
  const photoState = definition ? getStaticFieldState(definition.fields, "Photo") : null;

  if (photoState === null || photoState === "Hidden") {
    return null;
  }

  return (
    <Card className="align-items-center flex gap-4 rounded-2xl p-4 md:p-6">
      <CustomerPhotoSection
        selectedUser={selectedUser}
        buttonSize={buttonSize}
        isRequired={photoState === "Mandatory"}
      />
    </Card>
  );
}
